<template>
  <section class="mx-auto max-w-[1440px] space-y-6 px-4 py-8 sm:px-6 lg:px-8">
    <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
      Loading venue details...
    </div>

    <div v-else class="space-y-6">
 

      <section class="overflow-hidden rounded-[32px] border border-[#eadfd4] bg-white shadow-sm">
        <div class="border-b border-[#efe3d8] px-6 py-5 lg:px-8">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.22em] text-[#b78969]">Public venue detail</p>
              <h1 class="mt-2 font-serif text-[2rem] font-semibold leading-tight text-ink lg:text-[2.6rem]">{{ venue.title }}</h1>
              <p class="mt-3 text-[1.05rem] italic text-[#927660]">{{ venue.tagLine || 'Venue details available for your event invite.' }}</p>
            </div>
            <span class="rounded-full bg-[#fbf1e6] px-4 py-2 text-sm font-semibold text-brand">
              {{ venue.typeLabel }}
            </span>
          </div>
        </div>

        <div class="space-y-6 px-6 py-6 lg:px-8">
          <div class="mx-auto max-w-[980px] grid gap-3 lg:grid-cols-12">
            <button
              type="button"
              class="relative min-h-[300px] overflow-hidden rounded-[28px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] p-4 text-left sm:min-h-[320px] lg:col-span-6 lg:min-h-[420px] lg:p-5"
              @click="openInviteGalleryById(heroGallery[0])"
            >
              <img v-if="heroGallery[0]" :src="heroGallery[0].src" :alt="venue.title" class="h-full w-full rounded-[22px] object-contain">
              <span v-if="heroGallery[0]?.isCover" class="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                Cover image
              </span>
            </button>

            <div class="grid gap-3 lg:col-span-3 lg:grid-rows-2">
              <button
                v-for="(image, index) in heroGallery.slice(1, 3)"
                :key="`${image.id || 'hero-middle'}-${image.src || index}-${index}`"
                type="button"
                class="relative min-h-[300px] overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] p-3 text-left sm:min-h-[300px] lg:min-h-[300px]"
                @click="openInviteGalleryById(image)"
              >
                <img :src="image.src" :alt="`${venue.title} ${index + 2}`" class="h-full w-full rounded-[18px] object-contain">
              </button>
            </div>

            <div class="grid gap-3 lg:col-span-3 lg:grid-rows-2">
              <button
                v-for="(image, index) in heroGallery.slice(3, 5)"
                :key="`${image.id || 'hero-right'}-${image.src || index}-${index}`"
                type="button"
                class="relative min-h-[300px] overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] p-3 text-left sm:min-h-[300px] lg:min-h-[300px]"
                @click="openInviteGalleryById(image)"
              >
                <img :src="image.src" :alt="`${venue.title} ${index + 4}`" class="h-full w-full rounded-[18px] object-contain">
                <div
                  v-if="image.overlayText"
                  class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
                >
                  <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                  <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
                </div>
              </button>
            </div>
          </div>

          <div class="rounded-[28px] border border-[#eadfd4] bg-[#fffdf9] p-6">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#6f5948]">
              <span class="inline-flex items-center gap-2">
                <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                {{ venue.address }}
              </span>
              <span class="text-[#d8c8bb]">·</span>
              <span>{{ establishedLabel }}</span>
              <span v-if="venue.phone" class="text-[#d8c8bb]">·</span>
              <span v-if="venue.phone" class="inline-flex items-center gap-2">
                <i class="ri-phone-line text-brand"></i>
                {{ venue.phone }}
              </span>
            </div>

            <p class="mt-6 text-[1.05rem] leading-8 text-[#2f241b]">
              {{ venue.about || 'Venue description is not available yet.' }}
            </p>

            <div v-if="venue.highlights.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Highlights</p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span v-for="item in venue.highlights" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                  {{ item }}
                </span>
              </div>
            </div>

            <div v-if="venue.inHouseServices.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
              <div class="mt-4 grid gap-3 md:grid-cols-2">
                <div v-for="item in venue.inHouseServices" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                  <span class="text-[#2e8a5d]">✓</span>
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div class="space-y-6">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue spaces</h2>
            </div>

            <div v-if="spaces.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in spaces" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <p class="mt-1 text-sm text-muted">{{ item.typeLabel }} · {{ item.setting }}</p>
                  </div>
                  <p class="text-base font-semibold text-[#1f1208]">{{ item.priceLabel }}</p>
                </div>
                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>
                <div v-if="item.seatingSummary.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="seat in item.seatingSummary" :key="seat" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ seat }}
                  </span>
                </div>
              </article>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No spaces configured yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div v-if="packages.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in packages" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                      <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                      <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                    </div>
                  </div>
                </div>
                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>
                <div v-if="item.includedServices.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="service in item.includedServices" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ service }}
                  </span>
                </div>
              </article>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No packages configured yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue policies</h2>
            </div>

            <div class="mt-6 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in venueRuleItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside class="self-start xl:sticky xl:top-24">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Enquiry</p>
            <h2 class="mt-2 font-serif text-[1.75rem] font-semibold text-ink">Ask about this venue</h2>
            <p class="mt-2 text-sm leading-7 text-muted">Share your event basics and venue requirements. We will submit your enquiry for this venue.</p>

            <form class="mt-5 space-y-4" @submit.prevent="submitVenueEnquiry">
              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Full name
                <input v-model.trim="enquiryForm.name" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>

              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Email
                <input v-model.trim="enquiryForm.email" type="email" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>

              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Phone
                <input v-model.trim="enquiryForm.phone" type="tel" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Event date
                  <input v-model="enquiryForm.eventDate" type="date" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>

                <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Guest count
                  <input v-model.trim="enquiryForm.guestCount" type="text" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
              </div>

              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Requirements
                <textarea v-model.trim="enquiryForm.details" rows="5" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none" placeholder="Tell us about your event type, budget range, seating needs, and any must-have requirements."></textarea>
              </label>

              <p v-if="enquiryError" class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {{ enquiryError }}
              </p>
              <p v-if="enquirySuccess" class="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {{ enquirySuccess }}
              </p>

              <button
                type="submit"
                :disabled="enquiryLoading"
                class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {{ enquiryLoading ? 'Sending enquiry...' : 'Send enquiry' }}
              </button>
            </form>
          </section>
        </aside>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/user/api'
import { getAuthUser } from '@/services/auth'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const venue = ref({
  id: '',
  title: '',
  tagLine: '',
  about: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  typeLabel: 'Venue',
  yearEstablished: '',
  highlights: [],
  inHouseServices: []
})
const gallery = ref([])
const spaces = ref([])
const packages = ref([])
const inviteGalleryLightbox = ref(null)
const authUser = getAuthUser() || {}
const enquiryLoading = ref(false)
const enquiryError = ref('')
const enquirySuccess = ref('')
const enquiryForm = ref({
  name: authUser?.name || authUser?.s_name || '',
  email: authUser?.email || authUser?.s_email || '',
  phone: authUser?.phone || authUser?.s_phone || '',
  eventDate: '',
  guestCount: '',
  details: ''
})
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const normalizeGallery = (items = []) =>
  items
    .map((item, index) => ({
      id: String(item?.id || `${item?.image || 'gallery'}-${index}`),
      src: String(item?.image || '').trim(),
      isCover: String(item?.is_cover || '0') === '1'
    }))
    .filter((item) => item.src)
    .sort((left, right) => Number(right.isCover) - Number(left.isCover))

const placeholderGallery = Array.from({ length: 5 }).map((_, index) => ({
  id: `placeholder-${index}`,
  src: `https://picsum.photos/seed/venue-${index + 1}/900/700`,
  isCover: index === 0,
  isPlaceholder: true
}))

const lightboxGalleryImages = computed(() => {
  const source = gallery.value.length ? gallery.value : placeholderGallery
  const hiddenCount = Math.max(0, source.length - 5)

  return source.map((item, index) => ({
    ...item,
    overlayText: hiddenCount && index === 4 ? `+${hiddenCount}` : ''
  }))
})

const heroGallery = computed(() => lightboxGalleryImages.value.slice(0, 5))
const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Year not specified')

const venueRuleItems = computed(() => [
  { icon: '🍽️', label: 'Catering', value: policies.value.inHouseCatering || 'Not specified' },
  { icon: '🌸', label: 'Decoration', value: policies.value.decorPolicy || 'Not specified' },
  { icon: '🎵', label: 'DJ / Music', value: policies.value.musicPolicy || 'Not specified' },
  { icon: '🥂', label: 'Alcohol', value: policies.value.alcoholInhouse === '1' ? 'In-house alcohol available.' : 'Alcohol policy not specified.' },
  { icon: '📋', label: 'House rules', value: policies.value.otherHouseRules || 'Not specified' }
])

const bookingTermItems = computed(() => [
  { icon: '👥', label: 'Guest count', value: policies.value.minGuests || policies.value.maxGuests ? `${policies.value.minGuests || '0'} - ${policies.value.maxGuests || '0'} guests` : 'Not specified' },
  { icon: '🛠️', label: 'Setup time', value: policies.value.decorTime || 'Not specified' },
  { icon: '🧹', label: 'Cleanup time', value: policies.value.cleanTime || 'Not specified' },
  { icon: '💳', label: 'Deposit', value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total` : 'Not specified' },
  { icon: '↩️', label: 'Cancellation', value: policies.value.cancelTerms || 'Not specified' }
])

const destroyInviteGallery = () => {
  if (inviteGalleryLightbox.value) {
    inviteGalleryLightbox.value.destroy(true)
    inviteGalleryLightbox.value = null
  }
}

const openInviteGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyInviteGallery()
  inviteGalleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.src
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false
  })
  inviteGalleryLightbox.value.openGallery(startIndex)
}

const openInviteGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openInviteGallery(startIndex >= 0 ? startIndex : 0)
}

const redirectToNotFound = () => {
  router.replace({
    name: 'frontend-404',
    query: {
      from: 'venue',
      venue_id: String(route.params.venueId || '')
    }
  })
}

const submitVenueEnquiry = async () => {
  enquiryError.value = ''
  enquirySuccess.value = ''

  if (!enquiryForm.value.name || !enquiryForm.value.email || !enquiryForm.value.phone || !enquiryForm.value.details) {
    enquiryError.value = 'Name, email, phone, and requirements are required.'
    return
  }

  enquiryLoading.value = true
  try {
    const formData = new FormData()
    formData.append('venue_id', venue.value.id || String(route.params.venueId || ''))
    formData.append('venue_name', venue.value.title || '')
    formData.append('name', enquiryForm.value.name)
    formData.append('email', enquiryForm.value.email)
    formData.append('phone', enquiryForm.value.phone)
    formData.append('event_date', enquiryForm.value.eventDate || '')
    formData.append('guest_count', enquiryForm.value.guestCount || '')
    formData.append('description', enquiryForm.value.details)
    formData.append('contextual_categories', venue.value.typeLabel || 'Venue')
    const { data } = await api.sendEnquiry(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Unable to send enquiry.')
    }
    enquirySuccess.value = data?.message || data?.msg || 'Enquiry sent successfully.'
    enquiryForm.value.details = ''
    enquiryForm.value.eventDate = ''
    enquiryForm.value.guestCount = ''
  } catch (err) {
    enquiryError.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Unable to send enquiry.'
  } finally {
    enquiryLoading.value = false
  }
}

const loadVenueDetail = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('venue_id', String(route.params.venueId || ''))
    const { data } = await api.getVenueDetailInvite(formData)
    const payload = data?.data || {}
    if (!payload || !Object.keys(payload).length) {
      redirectToNotFound()
      return
    }

    venue.value = {
      id: String(payload?.id || route.params.venueId || ''),
      title: payload?.title || 'Untitled venue',
      tagLine: payload?.tag_line || '',
      about: payload?.about || '',
      address: payload?.address || 'Location unavailable',
      phone: payload?.phone || '',
      email: payload?.email || '',
      website: payload?.website || '',
      typeLabel:  payload?.type || 'Venue',
      yearEstablished: payload?.est_year || '',
      highlights: splitToArray(payload?.highlights),
      inHouseServices: splitToArray(payload?.in_house_services)
    }

    gallery.value = normalizeGallery(payload?.gallery_info || [])
    spaces.value = Array.isArray(payload?.spaces_info)
      ? payload.spaces_info.map((item, index) => ({
          id: String(item?.id || `space-${index}`),
          name: item?.name || 'Untitled space',
          typeLabel: item?.type_name || item?.type || 'Space',
          setting: item?.setting || 'Indoor',
          description: item?.description || '',
          seatingSummary: splitToArray(item?.seat_config).map((label, seatIndex) => {
            const capacities = splitToArray(item?.seat_capacity)
            const capacity = capacities[seatIndex]
            return capacity ? `${label}: ${capacity}` : label
          }),
          priceLabel: [formatCurrency(item?.min_price), formatCurrency(item?.max_price)].filter(Boolean).join(' - ') || 'Price on request'
        }))
      : []

    packages.value = Array.isArray(payload?.venue_packages)
      ? payload.venue_packages.map((item, index) => ({
          id: String(item?.id || `package-${index}`),
          name: item?.name || 'Untitled package',
          description: item?.description || '',
          maxGuests: item?.max_guests || '',
          validHours: item?.valid_hours || '',
          includedServices: splitToArray(item?.included_services),
          priceLabel: formatCurrency(item?.price) || 'Price on request'
        }))
      : []

    policies.value = {
      inHouseCatering: payload?.venue_policies?.in_house_catering || '',
      outsideCatering: payload?.venue_policies?.outside_catering || '',
      decorPolicy: payload?.venue_policies?.decor_policy || '',
      musicPolicy: payload?.venue_policies?.music_policy || '',
      alcoholInhouse: String(payload?.venue_policies?.alcohol_inhouse || ''),
      alcoholOutside: String(payload?.venue_policies?.alcohol_outside || ''),
      minGuests: payload?.venue_policies?.min_guests || '',
      maxGuests: payload?.venue_policies?.max_guests || '',
      decorTime: payload?.venue_policies?.decor_time || '',
      cleanTime: payload?.venue_policies?.clean_time || '',
      noiseMusic: payload?.venue_policies?.noise_music || '',
      otherHouseRules: payload?.venue_policies?.other_house_rules || '',
      cancelTerms: payload?.venue_policies?.cancel_terms || '',
      depositPercent: payload?.venue_policies?.deposit_percent || ''
    }

    if (data?.status !== 'ok' && data?.msg) {
      if (/not available|not found|invalid/i.test(String(data.msg || ''))) {
        redirectToNotFound()
        return
      }
      error.value = data.msg
    }
  } catch (err) {
    const message = err?.message || 'Failed to load venue details.'
    if (/not available|not found|invalid/i.test(String(message))) {
      redirectToNotFound()
      return
    }
    error.value = message
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadVenueDetail()
})

onBeforeUnmount(() => {
  destroyInviteGallery()
})
</script>
